import React, { useEffect, useRef, useState } from "react";
import { PhoneIncoming, PhoneOff, User, Volume2 } from "lucide-react";
import {
  collection,
  limit,
  onSnapshot,
  query,
  where,
} from "firebase/firestore";
import { app, db } from "../Admin/Config/Config";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import ringtone from "./assets/ringtone.mp3";
import {
  LocalUser,
  RemoteUser,
  useIsConnected,
  useJoin,
  useLocalMicrophoneTrack,
  usePublish,
  useRemoteUsers,
  useRTCClient,
} from "agora-rtc-react";
import { getFunctions, httpsCallable } from "firebase/functions";
import CallConnectedPage from "./CallConnectedPage";
export default function IncomingCall({ status,onCall}) {
  const audioRef = useRef(null);
  const [incomingCall, setIncomingCall] = useState(null);
  const [visible, setVisible] = useState(false);
  const [isAudioUnlocked, setIsAudioUnlocked] = useState(false);
  const [channelName, setChannelName] = useState("");
  const [reciever, setReciever] = useState(null);
  const auth = getAuth(app);
  const client = useRTCClient();

  // ============== Function to play the ringtone ============ //
  const playRingtone = () => {
    if (audioRef.current) {
      audioRef.current.loop = true;
      audioRef.current.play().catch((err) => {
        console.log("🚨 Failed to play audio:", err);
        if (!isAudioUnlocked) {
          setVisible(true);
        }
      });
    }
  };

  const stopRingtone = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      audioRef.current.loop = false;
    }
  };

// ============== Function to unlock the audio ============ //
  const unlockAudio = () => {
    if (audioRef.current && !isAudioUnlocked) {
      audioRef.current
        .play()
        .then(() => {
          audioRef.current.pause();
          audioRef.current.currentTime = 0;
          setIsAudioUnlocked(true);
          console.log("✅ Audio permission granted.");
          if (incomingCall?.status === "ringing" && status === true) {
            playRingtone();
          }
        })
        .catch((err) => {
          console.log("🚨 Audio permission blocked:", err);
        });
    }
  };

  useEffect(() => {
    if (!audioRef.current) {
      audioRef.current = new Audio(ringtone);
      audioRef.current.preload = "auto";
    }
    return () => {
      stopRingtone();
    };
  }, [status]);

  useEffect(() => {
    const handleUserInteraction = () => {
      unlockAudio();
    };

    document.addEventListener("click", handleUserInteraction);

    return () => {
      document.removeEventListener("click", handleUserInteraction);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [incomingCall, isAudioUnlocked]);

  // ================ Incoming Call ================ //
  useEffect(() => {
    const authUnsub = onAuthStateChanged(auth, (user) => {
      if (!user) return;

      const uid = user.uid;
      const callRef = collection(db, "bristu_talk_call_invites");
      const q = query(callRef, where("receiver_uid", "==", uid),where("status", "==", "ringing"), limit(1));

      const unsubscribe = onSnapshot(q, (snapshot) => {
        if (snapshot.empty) {
          setVisible(false);
          setIncomingCall(null);
          stopRingtone();
          return;
        }

        const data = snapshot.docs[0]?.data();
        if (data?.status === "ringing" && status === true) {
          setVisible(true);
          setIncomingCall({
            id: snapshot.docs[0].id,
            ...data,
          });
          setChannelName(data?.channel_name);
          playRingtone();
        } else {
          // endCall();
          stopRingtone();
          setIncomingCall(null);
          setVisible(false);
          setIncomingCall(null);
        }
      });

      return () => {
        unsubscribe();
        authUnsub();
      };
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth, status]);

  const [isMuted, setIsMuted] = useState(false);
  const [inCall, setInCall] = useState(false);
  const [callConnection, setCallConnection] = useState({
    app_id: "",
    channel_name: "",
    token: "",
    uid: "",
  });

  const isConnected = useIsConnected();

  // Join the channel only when callConnection is fully populated
  useJoin(
    callConnection &&
      callConnection.token &&
      callConnection.app_id &&
      callConnection.channel_name
      ? {
          appid: callConnection.app_id,
          channel: callConnection.channel_name,
          token: callConnection.token,
        }
      : null,
    inCall
  );

  // Use the local microphone track hook
  const {
    localMicrophoneTrack,
    isLoading: isTrackLoading,
    error: trackError,
  } = useLocalMicrophoneTrack();

  // Publish the track only when it's ready
  usePublish(localMicrophoneTrack ? [localMicrophoneTrack] : []);

  // Handle mute/unmute without recreating the track
  useEffect(() => {
    if (localMicrophoneTrack) {
      localMicrophoneTrack.setEnabled(!isMuted, { ANS: true, AEC: true });
    }
  }, [isMuted, localMicrophoneTrack]);

  const remoteUsers = useRemoteUsers();

  // Subscribe to remote user audio tracks
  useEffect(() => {
    const handleUserPublished = async (user, mediaType) => {
      if (mediaType === "audio") {
        await client.subscribe(user, mediaType);
        // console.log(`Subscribed to audio track of user ${user.uid}`);
        user.audioTrack?.play();
      }
    };

    const handleUserUnpublished = (user, mediaType) => {
      if (mediaType === "audio") {
        // console.log(`Unsubscribed from audio track of user ${user.uid}`);
      }
    };

    client.on("user-published", handleUserPublished);
    client.on("user-unpublished", handleUserUnpublished);

    return () => {
      client.off("user-published", handleUserPublished);
      client.off("user-unpublished", handleUserUnpublished);
    };
  }, [client]);

  // Cleanup tracks on unmount
  useEffect(() => {
    return () => {
      if (localMicrophoneTrack) {
        localMicrophoneTrack.close();
      }
    };
  }, [localMicrophoneTrack]);

  // ================ ACCEPTING CALL ================ //
  const [isLoading, setIsLoading] = useState(false);
  const acceptCall = async () => {
    setIsLoading(true);
    try {
      const functions = getFunctions(app, "asia-south1");
      const createToken = httpsCallable(functions, "createTokenAgoraReceiver");
      const result = await createToken({
        channel_name: incomingCall.channel_name,
        sender_uid: incomingCall.caller_uid,
        token: incomingCall.token,
        topic: incomingCall.topic,
        receiver_uid: auth.currentUser.uid,
      });

      if (result.data.code === 200) {
        const data = result.data;
        setCallConnection({
          app_id: data.app_id,
          token: data.token,
          channel_name: data.channel_name,
          topic: data.topic,
          uid: data.uid,
        });
        setInCall(true);
        setIsLoading(false);
        onCall(true);
        stopRingtone();
      } else {
        throw new Error(result.data.message);
      }
    } catch (err) {
      console.error("Failed to accept call:", err);
    }
  };

  const endCall = async () => {
    setIsLoading(false);
    onCall(false);
    if(channelName){
      const functions = getFunctions(app, "asia-south1");
      const endCalling = httpsCallable(functions, "endCall");
      try {
        await endCalling({ channel_name: channelName});
        setIncomingCall(null);
        setChannelName("");
        setVisible(false);
        setInCall(false);
        setIsLoading(false);
        stopRingtone(); 
      } catch (err) {
        console.error("Error calling function:", err.message);
      }
      
    }
  };
  useEffect(()=>{
    if(remoteUsers.length>0){
      setReciever(remoteUsers);
    }
    if(remoteUsers.length===0 && reciever){
      endCall();
      setReciever(null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[remoteUsers])


  // Handle microphone permission errors
  if (trackError) {
    // console.error("Failed to access microphone:", trackError);
    return <div>Please grant microphone permissions to continue the call.</div>;
  }

  return (
    <>
      {isConnected ? (
        <div className="flex items-center  max-w-7xl justify-center min-h-screen">
          <div className="px-8 py-8 bg-black max-w-7xl rounded-2xl flex flex-col items-center justify-center w-full">
            {isTrackLoading ? (
              <div>Loading audio track...</div>
            ) : (
              <>
                <p>{"Topic:- "+callConnection?.topic ?? ""}</p>
                <CallConnectedPage
                  isConnected={isConnected}
                  onMute={() => setIsMuted(!isMuted)}
                  channelName={channelName ?? ""}
                  isLoading={isLoading}
                  endCall={endCall}
                />
                {localMicrophoneTrack && (
                  <LocalUser
                    audioTrack={localMicrophoneTrack}
                    micOn={!isMuted}
                    cover="https://www.agora.io/en/wp-content/uploads/2022/10/3d-spatial-audio-icon.svg"
                    playAudio={false}
                  >
                    <samp className="user-name">You</samp>
                  </LocalUser>
                )}
                {remoteUsers.map((user) => (
                  <div className="user" key={user.uid}>
                    <RemoteUser
                      cover="https://www.agora.io/en/wp-content/uploads/2022/10/3d-spatial-audio-icon.svg"
                      user={user}
                    >
                      <samp className="user-name">{user.uid}</samp>
                    </RemoteUser>
                  </div>
                ))}
              </>
            )}
          </div>
        </div>
      ) : visible ? (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-md flex items-center justify-center z-50 animate-fade-in">
          <div className="bg-white/95 p-10 rounded-2xl shadow-2xl flex flex-col items-center space-y-8 w-96 transform transition-all">
            <div className="relative">
              <div className="w-28 h-28 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center shadow-md">
                <User className="w-14 h-14 text-white" strokeWidth={1.5} />
              </div>
              <div className="absolute -bottom-3 -right-3 bg-green-500 w-8 h-8 rounded-full border-4 border-white flex items-center justify-center">
                <div className="animate-ping absolute inset-0 rounded-full bg-green-500 opacity-75" />
              </div>
            </div>

            <div className="text-center space-y-4">
              <p className="text-md text-gray-600 font-medium">Incoming Call</p>
              <h2 className="text-3xl font-bold text-gray-900">
                {incomingCall?.caller_name ?? "Unknown"}
              </h2>
              <p className="text-gray-500 text-sm"></p>
            </div>

            <div className="flex gap-12 mt-4">
              <button
                disabled={isLoading}
                onClick={endCall}
                className="relative group"
              >
                <div className="absolute -inset-2 bg-red-100/50 rounded-full animate-pulse" />
                <div
                  className="p-6 bg-red-600 text-white rounded-full shadow-lg transform transition-all 
              hover:scale-110 hover:shadow-xl group-hover:ring-4 group-hover:ring-red-300"
                >
                  <PhoneOff size={32} />
                </div>
              </button>

              <button
                disabled={isLoading}
                onClick={acceptCall}
                className="relative group"
              >
                <div
                  className="p-6 bg-green-600 text-white rounded-full shadow-lg transform transition-all 
              hover:scale-110 hover:shadow-xl group-hover:ring-4 group-hover:ring-green-300"
                >
                  <PhoneIncoming size={32} />
                </div>
                <div className="absolute -inset-1 rounded-full border-4 border-green-500 animate-ring-expand" />
              </button>
            </div>

            <div className="flex justify-center gap-6 mt-12">
              <button
                onClick={stopRingtone}
                className="flex items-center space-x-2 text-gray-600 hover:text-gray-800"
              >
                <Volume2 size={20} />
                <span>Silent</span>
              </button>
            </div>

            <p className="text-gray-500 text-sm mt-6">
              End-to-end encrypted call
            </p>
          </div>

          <style jsx global>{`
            @keyframes fade-in {
              0% {
                opacity: 0;
                transform: scale(0.95);
              }
              100% {
                opacity: 1;
                transform: scale(1);
              }
            }
            @keyframes ring-expand {
              0% {
                transform: scale(1);
                opacity: 1;
              }
              100% {
                transform: scale(1.4);
                opacity: 0;
              }
            }
            .animate-fade-in {
              animation: fade-in 0.3s cubic-bezier(0.4, 0, 0.2, 1);
            }
            .animate-ring-expand {
              animation: ring-expand 1.5s infinite cubic-bezier(0.4, 0, 0.2, 1);
            }
          `}</style>
        </div>
      ) : null}
    </>
  );
}
